import { ApolloClient, InMemoryCache } from "@apollo/client";
import { isCredentialRevokedClose } from "./apollo/connectionFailure";
import { makeAuthLink } from "./apollo/makeAuthLink";
import { makeErrorLink } from "./apollo/makeErrorLink";
import { makeHttpLink } from "./apollo/makeHttpLink";
import { makeRehydrateDatesLink } from "./apollo/makeRehydrateDatesLink";
import { makeRetryLink } from "./apollo/makeRetryLink";
import { makeSentryTraceLink } from "./apollo/makeSentryTraceLink";
import { makeSplitLink } from "./apollo/makeSplitLink";
import { makeSubscriptionsLink } from "./apollo/makeSubscriptionsLink";
import { reportServerSessionRejected } from "./auth/serverSessionRejection";
import { backendConnectionStore } from "./connection/backendConnectionStore";
import { getBackendUrl } from "./getBackendUrl";
import { translate } from "./i18n/TranslationBackendRegistry";
import { getGlobalNotifier } from "./notifications/notifier";
// import { getAuthToken } from "./util/keycloak";

/**
 * How long after a close for a revoked credential refused subscriptions are
 * not shown as errors.
 */
export const CREDENTIAL_REVOKED_QUIET_MS = 10_000;

export const initApolloClient = (
  getAuthToken: () => Promise<string | null>,
) => {
  return initApolloClient2(getAuthToken).apolloClient;
};

export const initApolloClient2 = (
  getAuthToken: () => Promise<string | null>,
) => {
  const { graphQlUrl, graphQlSubscriptionUrl } = getBackendUrl();

  const connectionKey = "ws-connection";
  type ToastState = "none" | "reconnecting" | "final";
  let toastState: ToastState = "none";

  const showReconnecting = () => {
    if (toastState === "final") return;
    if (toastState === "reconnecting") return;
    toastState = "reconnecting";
    getGlobalNotifier().notify(
      "warning",
      translate("framework.connection.lost", "Verbindung unterbrochen"),
      {
        key: connectionKey,
        persist: true,
        description: "Erneut verbinden…",
      },
    );
  };

  const showDefinitivelyLost = () => {
    if (toastState === "final") return;
    toastState = "final";
    getGlobalNotifier().notify(
      "error",
      translate("framework.connection.lost", "Verbindung unterbrochen"),
      {
        key: connectionKey,
        persist: true,
        bypassDedupe: true,
        description: translate(
          "framework.connection.unreachable",
          "Keine Verbindung zum Server möglich. Bitte Seite neu laden.",
        ),
      },
    );
  };

  /** When the server last closed the connection for a revoked credential. */
  let credentialRevokedAt: number | undefined;
  /** The client closed itself (dispose): no lost connection to show. */
  let disposed = false;
  /**
   * Shortly after a close for a revoked credential the connection is closed
   * again on purpose (a refused subscription reconnects it, the no-access
   * screen disposes the client): no lost connection to show then.
   */
  /**
   * A refused credential is no lost connection: the auth integration takes
   * over, and a connection toast still shown from before goes.
   */
  const handleCredentialRevoked = () => {
    credentialRevokedAt = Date.now();
    if (toastState === "reconnecting") {
      toastState = "none";
      getGlobalNotifier().dismiss(connectionKey);
    }
    reportServerSessionRejected();
  };

  const isQuietAfterRevocation = () =>
    credentialRevokedAt !== undefined &&
    Date.now() - credentialRevokedAt < CREDENTIAL_REVOKED_QUIET_MS;

  // Assume we start in a connecting state until the WS link reports connected.
  backendConnectionStore.setConnecting();

  const wsLink = makeSubscriptionsLink(
    graphQlSubscriptionUrl,
    getAuthToken,
    10,
    5_000,
    {
      onDelayedConnection: () => {
        backendConnectionStore.setConnecting();
        // Avoid a second notification here. We'll show a single persistent
        // "Connection lost / Reconnecting…" toast from the closed/error handlers.
      },
      connecting: () => {
        backendConnectionStore.setConnecting();
      },
      connected: () => {
        backendConnectionStore.setConnected();
        toastState = "none";
        getGlobalNotifier().dismiss(connectionKey);
      },
      closed: (event) => {
        backendConnectionStore.setDisconnected();
        if (isCredentialRevokedClose(event)) {
          // Not a lost connection: the server no longer accepts the
          // credential. The reconnect reads the current token; the auth
          // integration decides whether the user signs in again.
          handleCredentialRevoked();
          return;
        }
        // The client's own close (dispose, for example when the no-access
        // screen replaces the app): nothing to reconnect.
        if (isQuietAfterRevocation() || disposed) return;
        showReconnecting();
      },
      error: () => {
        backendConnectionStore.setDisconnected();
        if (isQuietAfterRevocation()) return;
        showReconnecting();
      },
      onFatalError: (errorOrCloseEvent) => {
        backendConnectionStore.setFinalDisconnected();
        if (isCredentialRevokedClose(errorOrCloseEvent)) {
          // The server refused every connection: the auth integration takes
          // over, and the client does not connect again by itself (a
          // reload does).
          handleCredentialRevoked();
        }
        // This is called by graphql-ws when it gives up retrying (non-lazy).
        // Show a final, definitive message.
        console.error(
          "WebSocket fatal error (retry exhausted)",
          errorOrCloseEvent,
        );
        showDefinitivelyLost();
      },
    },
  );
  const client = (wsLink as { client?: { dispose: () => unknown } }).client;
  if (client) {
    const dispose = client.dispose.bind(client);
    client.dispose = () => {
      disposed = true;
      return dispose();
    };
  }

  const apolloClient = new ApolloClient({
    queryDeduplication: true,
    defaultOptions: {
      mutate: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
    },
    cache: new InMemoryCache(),
    link: makeSplitLink(
      [
        makeRetryLink(),
        makeRehydrateDatesLink(),
        makeAuthLink(getAuthToken),
        makeSentryTraceLink(),
        makeHttpLink(graphQlUrl),
      ],
      [
        makeErrorLink({
          // The socket keeps the token it connected with. After a renewed
          // token the server rejects new subscriptions on it: reconnect, so
          // that the connection parameters are read again. The connected
          // transition then reopens the subscriptions that were rejected.
          onSessionRejection: () => {
            wsLink.client.terminate();
            reportServerSessionRejected();
          },
          // The subscriptions the reconnect reopens after a revoked
          // credential are refused until the auth integration has caught
          // up: no toast for each of them.
          isQuiet: isQuietAfterRevocation,
        }),
        makeRehydrateDatesLink(),
        wsLink,
      ],
    ),
  });

  return { apolloClient, wsLink };
};
